import { useRef, useState } from "react";
import { Crosshair } from "lucide-react";
import api from "../../lib/api";
import { useFocalMap, getObjectPosition, getFocalPoint } from "../../context/FocalMapContext";
import { toast } from "sonner";

/**
 * Click-to-set focal point. The click area shows the WHOLE photo (never cropped), so a click always
 * maps to the real position in the image. Saved per image URL (shared everywhere that image is used).
 * Live previews underneath show how the photo is cropped on the site.
 */
export default function FocalPointPicker({ src, testIdPrefix = "focal", banner = false }) {
    const { map, refresh } = useFocalMap();
    const imgRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [justSaved, setJustSaved] = useState(false);

    if (!src) return null;
    const point = getFocalPoint(map, src);
    const objectPosition = getObjectPosition(map, src);

    const save = async (x, y) => {
        setSaving(true);
        try {
            await api.put("/admin/image-focal", { url: src, focal_x: x, focal_y: y });
            refresh();
            toast.success("Focal point saved — live on the site");
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 4000);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save focal point");
        } finally {
            setSaving(false);
        }
    };

    const onClick = (e) => {
        const el = imgRef.current;
        if (!el || saving) return;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
        save(x, y);
    };

    return (
        <div className="mt-2" data-testid={`${testIdPrefix}-picker`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-ink-soft hover:text-blush transition-colors"
                data-testid={`${testIdPrefix}-toggle`}
            >
                <Crosshair size={12} /> {open ? "Close focal point" : "Set focal point"}
            </button>
            {open && (
                <div className="mt-2">
                    <p className="text-xs text-ink-soft mb-2">
                        Click the part of the photo that must always stay visible (a face, the top of a cake). The pink dot shows the current spot.
                    </p>
                    <div className="relative inline-block max-w-sm w-full cursor-crosshair border border-[#EAE3DE] leading-none" onClick={onClick} data-testid={`${testIdPrefix}-area`}>
                        <img ref={imgRef} src={src} alt="Click to set focal point" className="block w-full h-auto select-none pointer-events-none" draggable={false} />
                        {point && (
                            <div
                                className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 border-white bg-blush/80 shadow pointer-events-none"
                                style={{ left: `${point[0]}%`, top: `${point[1]}%` }}
                                data-testid={`${testIdPrefix}-marker`}
                            />
                        )}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs h-5" data-testid={`${testIdPrefix}-status`}>
                        {saving ? (
                            <span className="text-ink-soft">Saving…</span>
                        ) : justSaved ? (
                            <span className="text-green-700">✓ Saved — live on the site now</span>
                        ) : point ? (
                            <span className="text-ink-soft">Focal point is set. Click again to change it.</span>
                        ) : (
                            <span className="text-ink-soft">No focal point set yet (the centre is used).</span>
                        )}
                        {point && !saving && (
                            <button type="button" onClick={() => save(50, 50)} className="underline text-ink-soft hover:text-blush" data-testid={`${testIdPrefix}-reset`}>
                                Reset to centre
                            </button>
                        )}
                    </div>

                    <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-previews`}>
                        {banner ? (
                            <>
                                <div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-ink-soft mb-1">How it looks — desktop banner</p>
                                    <div className="w-full max-w-xl overflow-hidden border border-[#EAE3DE]" style={{ aspectRatio: "6.2 / 1" }}>
                                        <img src={src} alt="Desktop banner preview" className="w-full h-full object-cover pointer-events-none" style={{ objectPosition }} />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-ink-soft mb-1">How it looks — phone banner</p>
                                    <div className="w-48 overflow-hidden border border-[#EAE3DE]" style={{ aspectRatio: "2.1 / 1" }}>
                                        <img src={src} alt="Phone banner preview" className="w-full h-full object-cover pointer-events-none" style={{ objectPosition }} />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex gap-4 items-end">
                                <div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-ink-soft mb-1">Portrait card</p>
                                    <div className="w-28 overflow-hidden border border-[#EAE3DE]" style={{ aspectRatio: "4 / 5" }}>
                                        <img src={src} alt="Portrait preview" className="w-full h-full object-cover pointer-events-none" style={{ objectPosition }} />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-ink-soft mb-1">Square</p>
                                    <div className="w-24 overflow-hidden border border-[#EAE3DE]" style={{ aspectRatio: "1 / 1" }}>
                                        <img src={src} alt="Square preview" className="w-full h-full object-cover pointer-events-none" style={{ objectPosition }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
